import { createEnv } from "@t3-oss/env-core"
import { z } from "zod"

export const env = createEnv({
  // Skip validation in development mode to allow testing pages without full backend config
  // Production always validates to catch missing env vars before deployment
  // Note: Brand config reads directly from process.env/import.meta.env to bypass this
  skipValidation:
    !!process.env.SKIP_ENV_VALIDATION ||
    process.env.NODE_ENV === "test" ||
    process.env.NODE_ENV === "development",
  server: {
    // Vercel Blob storage token (optional - only needed when STORAGE_TYPE=vercel)
    BLOB_READ_WRITE_TOKEN: z.string().optional(),
    BLUEPRINT_API_URL: z.url(),
    BLUEPRINT_API_KEY: z.string(),
    BLUEPRINT_RECOMMENDER_URL: z.url().default("http://localhost:5247"),
    BLUEPRINT_RECOMMENDER_API_KEY: z.string().optional(),
    DATABASE_URL: z.url(),
    MICROSOFT_CLIENT_ID: z.string(),
    MICROSOFT_CLIENT_SECRET: z.string(),
    MICROSOFT_TENANT_ID: z.string(),
    MICROSOFT_REDIRECT_URI: z.url().default("http://localhost:3000/api/auth/callback/microsoft"),
    // Better Auth configuration
    BETTER_AUTH_SECRET: z.string(),
    // OAuth Proxy: URL of the proxy server (dev environment)
    // Preview/production deployments use this as the OAuth redirect target
    BETTER_AUTH_URL: z.url().optional(),
    BETTER_AUTH_ACCOUNT_LINKING: z
      .string()
      .default("false")
      .transform((val) => val.toLowerCase() === "true"),
    STORAGE_TYPE: z.enum(["vercel", "filesystem", "azure"]),
    // Azure Storage configuration (optional - only needed when STORAGE_TYPE=azure)
    AZURE_STORAGE_CONNECTION_STRING: z.string().optional(),
    AZURE_STORAGE_CONTAINER: z.string().default("shelfi-files"),
    VERCEL: z.string().optional(),
    VERCEL_URL: z.string().optional(),
    VERCEL_BRANCH_URL: z.string().optional(),
    VERCEL_ENV: z.string().optional(),
    // A Cloudflare Worker Preview's own URL; BETTER_AUTH_URL there is the OAuth proxy
    CLOUDFLARE_PREVIEW_URL: z.url().optional(),
    VERCEL_PROJECT_PRODUCTION_URL: z.string().optional(),
    VERCEL_TARGET_ENV: z.string().optional(),
    // RBAC: First admin email (optional)
    // If set, only this email will be assigned admin role on first login
    // If not set, the first user to login becomes admin automatically
    FIRST_ADMIN_EMAIL: z.string().email().optional(),
  },
  clientPrefix: "VITE_",
  client: {
    VITE_SENTRY_DSN: z.string().optional(),
    VITE_SENTRY_TRACES_SAMPLE_RATE: z.string().optional(),
    // Auth
    VITE_AUTH_ENABLE_EMAIL: z
      .string()
      .default("false")
      .transform((val) => val.toLowerCase() === "true"),
    // Branding
    VITE_BRAND_NAME: z.string().optional(),
    VITE_BRAND_LOGO_LIGHT_URL: z.string().url().optional(),
    VITE_BRAND_LOGO_DARK_URL: z.string().url().optional(),
    VITE_BRAND_FAVICON_URL: z.string().url().optional(),
    VITE_BRAND_PRIMARY_LIGHT: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    VITE_BRAND_SECONDARY_LIGHT: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    VITE_BRAND_ACCENT_LIGHT: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    VITE_BRAND_PRIMARY_DARK: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    VITE_BRAND_SECONDARY_DARK: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    VITE_BRAND_ACCENT_DARK: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    // Foreground colors (text on colored backgrounds)
    VITE_BRAND_PRIMARY_FG_LIGHT: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    VITE_BRAND_PRIMARY_FG_DARK: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    VITE_BRAND_SECONDARY_FG_LIGHT: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    VITE_BRAND_SECONDARY_FG_DARK: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
  },
  runtimeEnv: Object.assign({}, import.meta.env, process.env),
  emptyStringAsUndefined: true,
})
